import type {MCFetcher} from '@github-ui/ahp-relay/mission-control/mission-control-environment-client'

const ACTIVATION_TRACE_ID_PATTERN = /^[0-9a-f]{32}$/i
const DATADOG_TRACE_BASE_URL = 'https://app.datadoghq.com/apm/trace/'
const EMPTY_TRACE_ID = ''
const MAX_TASKS = 5

const traceIdsByTask = new Map<string, string>()
const listenersByTask = new Map<string, Set<() => void>>()

export function getActivationTraceIdSnapshot(taskId: string | undefined): string {
  if (!taskId) return EMPTY_TRACE_ID
  return traceIdsByTask.get(taskId) ?? EMPTY_TRACE_ID
}

export function subscribeToActivationTraceId(taskId: string | undefined, listener: () => void): () => void {
  if (!taskId) return () => {}
  const listeners = listenersByTask.get(taskId) ?? new Set<() => void>()
  listeners.add(listener)
  listenersByTask.set(taskId, listeners)
  return () => {
    listeners.delete(listener)
    if (listeners.size === 0) listenersByTask.delete(taskId)
  }
}

export function recordActivationTraceId(taskId: string, traceId: unknown): void {
  if (typeof traceId !== 'string' || !ACTIVATION_TRACE_ID_PATTERN.test(traceId)) return
  if (traceIdsByTask.get(taskId) === traceId) return
  traceIdsByTask.delete(taskId)
  traceIdsByTask.set(taskId, traceId)

  for (const staleTaskId of [...traceIdsByTask.keys()].slice(0, Math.max(0, traceIdsByTask.size - MAX_TASKS))) {
    if (!listenersByTask.has(staleTaskId)) traceIdsByTask.delete(staleTaskId)
  }

  for (const listener of listenersByTask.get(taskId) ?? []) listener()
}

async function captureActivationTraceId(taskId: string, response: Response): Promise<void> {
  if (response.status !== 200 && response.status !== 202) return

  let body: unknown
  try {
    body = await response.clone().json()
  } catch {
    return
  }

  if (!body || typeof body !== 'object' || Array.isArray(body)) return
  recordActivationTraceId(taskId, (body as Record<string, unknown>).activation_trace_id)
}

export function createActivationTraceCapturingFetcher(fetcher: MCFetcher, taskId: string): MCFetcher {
  return async (path, options) => {
    const response = await fetcher(path, options)
    await captureActivationTraceId(taskId, response)
    return response
  }
}

export function activationTraceUrl(traceId: string): string | undefined {
  if (!ACTIVATION_TRACE_ID_PATTERN.test(traceId)) return undefined
  const url = new URL(encodeURIComponent(traceId), DATADOG_TRACE_BASE_URL)
  return url.toString()
}

export function clearActivationTraceIds(): void {
  const taskIds = [...traceIdsByTask.keys()]
  traceIdsByTask.clear()
  for (const taskId of taskIds) {
    for (const listener of listenersByTask.get(taskId) ?? []) listener()
  }
}
